import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { lazy, Suspense, useMemo, useState } from 'react';
import { lazily } from 'react-lazily';

import {
	getSubscriptions,
	GetSubscriptionsResponse,
	postSubscription,
} from 'services/account';

import { useErrorHandler } from 'hooks/use-error-handler';
import { useToast } from 'hooks/use-toast';

import { dynamicComposedComponent } from 'helpers/importer/dynamicComposedComponent';

import { LinkProps } from '@myaccount/components/subscription-card/subscription-card';
import { useSubscriptionStore } from '@myaccount/store';

import stylesDefault from '../styles.module.scss';
import styles from './subscriptions.module.scss';

const Loading = lazy(() => import('core/generic/elements/loading'));
const SubscriptionCard = lazy(
	() => import('@myaccount/components/subscription-card/subscription-card')
);
const Disclaimer = lazy(
	() => import('@myaccount/components/disclaimer/disclaimer')
);
const ModalWrapper = dynamicComposedComponent(
	() => import('core/generic/components/general/modal')
);
const ComponentSimpleHeader = lazy(
	() => import('core/generic/components/general/component-simple-header')
);
const { ButtonGroup } = lazily(
	() => import('core/generic/components/general/button-group')
);
const { Button } = lazily(() => import('core/generic/elements/button'));

type SubscriptionsProps = GetSubscriptionsResponse & {
	onSubmit?: (url: string) => void;
	isLoading?: boolean;
};

export default function Subscriptions() {
	const toast = useToast();
	const errorHandler = useErrorHandler();

	const { setButtonState } = useSubscriptionStore((state) => ({
		setButtonState: state.setButtonState,
	}));

	const query = useQuery({
		queryKey: ['subscriptions'],
		queryFn: () => getSubscriptions().then((response) => response.data),
	});

	const { subscriptions, privacyPolicyText } = query.data || {};

	const mutation = useMutation({
		mutationKey: ['subscriptions'],
		mutationFn: async (url: string) => await postSubscription(url),
		onError(error) {
			errorHandler.process(error, false);
			setButtonState({ id: '', isLoading: false });
		},
		onSuccess(response) {
			toast.success({ content: response.data.message });

			setButtonState({ id: '', isLoading: false });

			setTimeout(() => {
				query.refetch();
			}, 4000);
		},
	});

	if (query.isLoading) {
		return (
			<Suspense>
				<div className={stylesDefault.loading}>
					<Loading dimension="large" isWithCrest />
				</div>
			</Suspense>
		);
	}

	return (
		<Suspense>
			<SubscriptionsList
				subscriptions={subscriptions || []}
				privacyPolicyText={privacyPolicyText}
				onSubmit={mutation.mutateAsync}
				isLoading={mutation.isPending}
			/>
		</Suspense>
	);
}

const SubscriptionsList = ({
	subscriptions,
	privacyPolicyText,
	onSubmit,
}: SubscriptionsProps) => {
	const { setButtonState } = useSubscriptionStore((state) => ({
		setButtonState: state.setButtonState,
	}));

	const [modalSubscription, setModalSubscription] = useState<any>(null);
	const [isLoadingSubs, setIsLoadingSub] = useState<string[]>([] as string[]);
	const [modalVisible, setModalVisible] = useState(false);

	const activated = subscriptions.filter(
		(subscription) => subscription.status === 'Active'
	);

	const inactivated = subscriptions.filter(
		(subscription) => subscription.status !== 'Active'
	);

	const modalButtonTitle = useMemo(() => {
		if (modalSubscription?.status === 'Active') {
			return (
				modalSubscription?.activeSubscription?.cancelSubscription
					?.buttonLink?.title || 'Cancel subscription'
			);
		}

		if (modalSubscription?.status === 'Cancelled') {
			return (
				modalSubscription?.activeSubscription?.renewSubscription
					?.buttonLink?.title || 'Renew subscription'
			);
		}

		return '';
	}, [modalSubscription]);

	const modalTitle = useMemo(() => {
		if (modalSubscription?.status === 'Active') {
			return (
				modalSubscription?.activeSubscription?.cancelSubscription
					?.title || 'Cancel subscription'
			);
		}

		if (modalSubscription?.status === 'Cancelled') {
			return (
				modalSubscription?.activeSubscription?.renewSubscription
					?.title || 'Change subscription'
			);
		}

		return '';
	}, [modalSubscription]);

	const modalDescription = useMemo(() => {
		if (modalSubscription?.status === 'Active') {
			return (
				modalSubscription?.activeSubscription?.cancelSubscription
					?.description ||
				`Are you sure you want to cancel your ${modalSubscription?.title} subscription?`
			);
		}

		if (modalSubscription?.status === 'Cancelled') {
			return (
				modalSubscription?.activeSubscription?.renewSubscription
					?.description ||
				`Are you sure you want to renew your ${modalSubscription?.title} subscription?`
			);
		}

		return '';
	}, [modalSubscription]);

	const modalButtonsLoading = useMemo(
		() => isLoadingSubs?.includes(modalSubscription?.subscriptionId),
		[isLoadingSubs, modalSubscription]
	);

	const handleOnSubmit = async (data: any) => {
		try {
			setModalSubscription(data);
			setModalVisible(true);
		} catch (error) {
			if (axios.isAxiosError(error)) {
				console.error(
					'Axios error:',
					error.response?.data || error.message
				);
			} else {
				console.error('Unexpected error:', error);
			}
		}
	};

	const handleSubscriptionChanges = async () => {
		try {
			setIsLoadingSub((prevItems) => [
				...prevItems,
				modalSubscription?.subscriptionId,
			]);
			if (modalSubscription?.status === 'Cancelled') {
				/* renew subscription scenario */
				await onSubmit?.(
					modalSubscription?.Links?.filter(
						(link: LinkProps) =>
							link?.title === 'Renew subscription'
					)[0]?.url
				);
			}
			if (modalSubscription?.status === 'Active') {
				setButtonState({
					id: `link-${modalSubscription?.subscriptionId}-${modalSubscription?.activeSubscription?.cancelSubscription?.buttonLink?.gaAction}`,
					isLoading: true,
				});
				/* cancel subscription scenario */
				await onSubmit?.(
					modalSubscription?.activeSubscription?.cancelSubscription
						?.buttonLink?.url
				);
			}
			setIsLoadingSub((prevItems) =>
				prevItems.filter(
					(item) => item !== modalSubscription?.subscriptionId
				)
			);
		} catch (error) {
			if (axios.isAxiosError(error)) {
				console.error(
					'Axios error:',
					error.response?.data || error.message
				);
			} else {
				console.error('Unexpected error:', error);
			}
		}
	};

	return (
		<Suspense>
			<div className={`row ${stylesDefault.gap}`}>
				<div className="col-12">
					<h1 className="display-large">Subscriptions</h1>
				</div>

				<div className="col-12">
					<div className={`${styles.container} ${stylesDefault.gap}`}>
						{activated.length > 0 && (
							<div className={styles['list-subscriptions']}>
								<h2 className="title-small">
									Active subscriptions
								</h2>

								<div className="row">
									{activated.map((subscription) => (
										<div
											key={subscription.subscriptionId}
											className="col-12 col-md-4 col-xl-3"
										>
											<div
												className={styles['list-item']}
											>
												<SubscriptionCard
													showLoadingButton={true}
													data={subscription}
													imagePrefix="account"
													onUnsubscribe={() => {
														handleOnSubmit(
															subscription
														);
													}}
												/>
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						{inactivated.length > 0 && (
							<div className={styles['list-subscriptions']}>
								<h2 className="title-small">
									Explore subscriptions
								</h2>

								<div className="row">
									{inactivated.map((subscription) => (
										<div
											key={subscription.subscriptionId}
											className="col-12 col-md-4 col-xl-3"
										>
											<div
												className={styles['list-item']}
											>
												<SubscriptionCard
													data={subscription}
													imagePrefix="account"
													onRenew={() =>
														handleOnSubmit(
															subscription
														)
													}
												/>
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>

				{privacyPolicyText && (
					<Disclaimer
						disclaimerText={privacyPolicyText}
						className="col-12"
					/>
				)}

				<ModalWrapper>
					{(Modal) => (
						<Modal.Root
							setVisible={setModalVisible}
							visible={modalVisible}
							size="small"
						>
							<Modal.Header>
								<ComponentSimpleHeader
									title={modalTitle}
									visible={modalVisible}
								/>
							</Modal.Header>
							<Modal.Body>
								<p>{modalDescription}</p>
							</Modal.Body>
							<Modal.Footer>
								<ButtonGroup
									orientation="horizontal"
									spacing="medium"
								>
									<Button
										type="button"
										onClick={() => {
											handleSubscriptionChanges();
											setModalVisible(false);
										}}
										label={modalButtonTitle}
										variant="primary"
										fullWidth
										ariaLabel={modalButtonTitle}
									/>
									<Button
										type="button"
										onClick={() => setModalVisible(false)}
										label="Back"
										variant="tertiary"
										fullWidth
										ariaLabel="Back"
									/>
								</ButtonGroup>
							</Modal.Footer>
						</Modal.Root>
					)}
				</ModalWrapper>
			</div>
		</Suspense>
	);
};
